/**
 * split-pdf-plan.js — SplitPdfPlan: which pages go into which file, and what
 * each file is called.
 *
 * ONE implementation for both sides of /split-pdf: the page calls it to show
 * the names before the button is pressed, the worker calls it to cut the
 * document. The names a person reads under the pattern field are therefore
 * the names that land in the archive — not a second guess of them.
 *
 * WHY THIS EXISTS (bug report 2026-09-25, /ru/split-pdf, account #7473):
 *   «Отправляю файл pdf для разделения на части, приходит архив только с
 *    последней частью».
 *   The person first used «Извлечь страницы» and typed the name they wanted,
 *   «История» — for ONE output file that is exactly right. The name field is
 *   shared by all five modes, so it followed them to «Каждые N страниц», where
 *   26 parts were all rendered as «История.pdf». JSZip.file() with a name it
 *   already holds REPLACES the entry, so the archive carried the last part
 *   only — 223 KB where the parts weigh 12 MB — and the button said
 *   «✓ Файлы сохранены». Measured on both shards, 90 days: 18 of 51 archives
 *   (35 %) came out like that, in 5 sessions; this reporter pressed the button
 *   ten times over two days and only got the parts from a fresh tab, where the
 *   field was empty again.
 *
 * The rule: names inside one output set are DISTINCT, by construction. When
 * the pattern would give two files the same name — no varying token, an empty
 * {title}, two bookmarks with one title, two names that differ only in case
 * (one file on Windows and macOS), two long names cut to the same prefix —
 * every file gets its part number: «История-1.pdf … История-26.pdf». A single
 * output keeps the name exactly as typed.
 *
 * Depends on PdfDocHelpers.parsePageRange (pdf-doc-helpers.js) and
 * window.__outputName (output-filename.js), resolved at call time so load
 * order does not matter. UMD: browser/worker global + Node for the tests
 * (tests/js/split-pdf-plan.test.mjs).
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.SplitPdfPlan = api;
})(typeof self !== 'undefined' ? self : globalThis, function (root) {
    'use strict';

    var EXT = 'pdf';
    var TITLE_BYTES = 120;   // a bookmark title inside a name; {name} needs room too

    function deps() {
        var H = root.PdfDocHelpers, ON = root.__outputName;
        if (!H || typeof H.parsePageRange !== 'function' || !ON || typeof ON.join !== 'function') {
            throw new Error('SPLIT_PLAN_DEPS');
        }
        return { parse: H.parsePageRange, ON: ON };
    }

    function defaultPattern(mode) {
        switch (mode) {
            case 'individual':   return '{name}-page-{page}.pdf';
            case 'extract':      return '{name}-extracted.pdf';
            case 'ranges':       return '{name}-part-{idx}.pdf';
            case 'every-n':      return '{name}-part-{idx}.pdf';
            case 'by-bookmarks': return '{name}-{title}.pdf';
        }
        return '{name}-{idx}.pdf';
    }

    function stripExt(name) {
        return String(name || 'document').replace(/\.[^.]+$/, '') || 'document';
    }

    function render(pattern, v) {
        var tok = function (x) { return x == null ? '' : String(x); };
        return String(pattern)
            .replace(/\{name\}/g, tok(v.name))
            .replace(/\{idx\}/g, tok(v.idx))
            .replace(/\{page\}/g, tok(v.page))
            .replace(/\{start\}/g, tok(v.start))
            .replace(/\{end\}/g, tok(v.end))
            .replace(/\{title\}/g, tok(v.title));
    }

    /** Which pages go into which part. Throws the worker's error codes. */
    function partitions(o, parse) {
        var pc = o.pageCount | 0;
        if (pc <= 0) throw new Error('EMPTY');
        var mode = o.mode;
        var out = [];

        if (mode === 'individual') {
            for (var i = 0; i < pc; i++) out.push({ pageIndices: [i], idx: i + 1, title: null });
            return out;
        }
        if (mode === 'extract') {
            var ex = parse(o.extractRange || '', pc);
            if (ex.length === 0) throw new Error('EMPTY_RANGE');
            return [{ pageIndices: ex, idx: 1, title: null }];
        }
        if (mode === 'ranges') {
            var rs = Array.isArray(o.ranges) ? o.ranges.filter(function (r) { return String(r || '').trim(); }) : [];
            if (rs.length === 0) throw new Error('NO_RANGES');
            rs.forEach(function (r, k) {
                var ix = parse(r, pc);
                if (ix.length) out.push({ pageIndices: ix, idx: k + 1, title: null });
            });
            if (out.length === 0) throw new Error('EMPTY_RANGES');
            return out;
        }
        if (mode === 'every-n') {
            var n = Math.max(1, parseInt(o.everyN, 10) || 1);
            for (var s = 0; s < pc; s += n) {
                var ix2 = [];
                for (var j = s; j < Math.min(s + n, pc); j++) ix2.push(j);
                out.push({ pageIndices: ix2, idx: Math.floor(s / n) + 1, title: null });
            }
            return out;
        }
        if (mode === 'by-bookmarks') {
            // Top-level outline entries with a resolved page, in page order.
            // Each starts a part that runs to the next one; pages before the
            // first bookmark are not a part (predictable, as Sejda does it).
            var pts = (o.points || []).filter(function (p) {
                return p && p.pageIndex >= 0 && p.pageIndex < pc;
            }).slice().sort(function (a, b) { return a.pageIndex - b.pageIndex; });
            if (pts.length === 0) throw new Error('NO_BOOKMARKS');
            for (var b = 0; b < pts.length; b++) {
                var st = pts[b].pageIndex;
                var en = b < pts.length - 1 ? pts[b + 1].pageIndex - 1 : pc - 1;
                var ix3 = [];
                for (var q = st; q <= en; q++) ix3.push(q);
                out.push({ pageIndices: ix3, idx: b + 1, title: pts[b].title });
            }
            return out;
        }
        throw new Error('UNKNOWN_MODE: ' + mode);
    }

    /**
     * The whole plan: parts + a distinct name for each.
     *   o = { mode, pageCount, points, srcName, pattern, ranges, everyN, extractRange }
     *   → { parts: [{ name, pageIndices }], numbered }
     * `numbered` — the pattern alone would have collided, so part numbers were
     * appended (the page shows it; telemetry counts it).
     */
    function plan(o) {
        var d = deps(), ON = d.ON;
        var raw = partitions(o, d.parse);
        var pattern = String(o.pattern || '').trim() || defaultPattern(o.mode);
        var srcBase = ON.sanitize(stripExt(o.srcName)) || 'document';
        var tail = new RegExp('\\.' + EXT + '\\s*$', 'i');

        var bases = raw.map(function (p) {
            var first = p.pageIndices[0] + 1, last = p.pageIndices[p.pageIndices.length - 1] + 1;
            var title = p.title == null ? '' : ON.truncBytes(ON.sanitize(p.title), TITLE_BYTES);
            var s = render(pattern, {
                name: stripExt(o.srcName), idx: p.idx, page: first, start: first, end: last,
                title: p.title == null ? '' : (title || 'untitled'),
            });
            return ON.sanitize(s.replace(tail, '')) || srcBase;
        });

        var names = bases.map(function (b) { return ON.join(b, EXT); });
        var numbered = false;
        if (names.length > 1) {
            var seen = Object.create(null);
            for (var i = 0; i < names.length; i++) {
                var k = names[i].toLowerCase();
                if (seen[k]) { numbered = true; break; }
                seen[k] = 1;
            }
        }
        if (numbered) {
            // The number goes on AFTER the base is cut to fit, so a long name
            // can never lose it. "-<idx>" with distinct idx cannot collide:
            // neither suffix can end the other (digits carry no dash).
            names = bases.map(function (b, k) {
                var suffix = '-' + raw[k].idx;
                var room = ON.MAX_BYTES - ON.utf8Len(suffix + '.' + EXT);
                return ON.truncBytes(b, room) + suffix + '.' + EXT;
            });
        }
        return {
            numbered: numbered,
            parts: raw.map(function (p, k) { return { name: names[k], pageIndices: p.pageIndices }; }),
        };
    }

    return { plan: plan, defaultPattern: defaultPattern };
});
